import {ValidationErrorKeys} from '@hconnect/common/consts'
import {TFunction} from 'i18next'
import {array, mixed, number, object, string, TestContext} from 'yup'

import {SlaTargetType} from '../types'

/**
 * Creates a Yup test function that validates uniqueness of names
 */
const createUniqueNameTest = (
  t: TFunction,
  messageKey: string,
  getItems: (value: unknown) => Array<{name: string | undefined; path: string}>
) => {
  return function (this: TestContext, value: unknown) {
    const items = getItems(value)
    const seen = new Map<string, string>()

    for (const {name, path} of items) {
      if (!name) continue
      const normalized = name.toLowerCase().trim()
      if (!normalized) continue

      if (seen.has(normalized)) {
        return this.createError({path: `${this.path}.${path}`, message: t(messageKey)})
      }
      seen.set(normalized, path)
    }
    return true
  }
}

/**
 * Validation schema for the Edit KPIs Modal form
 */
export const getEditKpiDefinitionsValidationSchema = (t: TFunction) => {
  const targetSchema = object().shape({
    unit: string().trim().required(t(ValidationErrorKeys.REQUIRED)),
    targetType: mixed<SlaTargetType>()
      .oneOf(Object.values(SlaTargetType))
      .required(t(ValidationErrorKeys.REQUIRED)),
    targetValue: number()
      .nullable()
      .when('targetType', {
        is: (val: SlaTargetType) =>
          val === SlaTargetType.GreaterThan || val === SlaTargetType.LessThan,
        then: (schema) =>
          schema
            .required(t(ValidationErrorKeys.REQUIRED))
            .typeError(t(ValidationErrorKeys.REQUIRED)),
        otherwise: (schema) => schema.nullable()
      }),
    minValue: number()
      .nullable()
      .when('targetType', {
        is: SlaTargetType.Range,
        then: (schema) =>
          schema
            .required(t(ValidationErrorKeys.REQUIRED))
            .typeError(t(ValidationErrorKeys.REQUIRED)),
        otherwise: (schema) => schema.nullable()
      }),
    maxValue: number()
      .nullable()
      .when('targetType', {
        is: SlaTargetType.Range,
        then: (schema) =>
          schema
            .required(t(ValidationErrorKeys.REQUIRED))
            .typeError(t(ValidationErrorKeys.REQUIRED))
            .test(
              'max-greater-than-min',
              t('editKpisModal.validation.maxGreaterThanMin'),
              function (maxValue) {
                const {minValue} = this.parent as {minValue: number | null}
                if (minValue === null || maxValue === null || maxValue === undefined) return true
                return maxValue > minValue
              }
            ),
        otherwise: (schema) => schema.nullable()
      })
  })

  const definitionSchema = object().shape({
    id: string().nullable(),
    name: string().trim().required(t(ValidationErrorKeys.REQUIRED)),
    kpiGroupId: string().nullable(),
    slaTarget: targetSchema,
    sourceKpiId: string().nullable()
  })

  const groupSchema = object().shape({
    id: string().nullable(),
    name: string().trim().required(t(ValidationErrorKeys.REQUIRED)),
    definitions: array().of(definitionSchema).default([])
  })

  type TabGroup = {
    groups?: Array<{name?: string; definitions?: Array<{name?: string}>}>
    ungroupedDefinitions?: Array<{name?: string}>
  }

  const tabGroupSchema = object()
    .shape({
      tabType: string().required(),
      groups: array().of(groupSchema).default([]),
      ungroupedDefinitions: array().of(definitionSchema).default([])
    })
    .test(
      'unique-group-names',
      t('editKpisModal.validation.duplicateGroupName'),
      createUniqueNameTest(t, 'editKpisModal.validation.duplicateGroupName', (value) => {
        const tab = value as TabGroup
        return (tab?.groups ?? []).map((g, i) => ({name: g.name, path: `groups[${i}].name`}))
      })
    )
    .test(
      'unique-kpi-names',
      t('editKpisModal.validation.duplicateKpiName'),
      createUniqueNameTest(t, 'editKpisModal.validation.duplicateKpiName', (value) => {
        const tab = value as TabGroup
        const items: Array<{name: string | undefined; path: string}> = []

        tab?.ungroupedDefinitions?.forEach((def, i) => {
          items.push({name: def.name, path: `ungroupedDefinitions[${i}].name`})
        })

        tab?.groups?.forEach((group, gi) => {
          group.definitions?.forEach((def, di) => {
            items.push({name: def.name, path: `groups[${gi}].definitions[${di}].name`})
          })
        })

        return items
      })
    )

  return object().shape({
    tabGroups: array().of(tabGroupSchema).default([])
  })
}
